import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import { adminApi } from 'utils/api';

const styles = {
    header: {
        mb: 2,
    },
    form: {
        display: 'flex',
        gap: 2,
        mb: 3,
        alignItems: 'flex-end',
    },
    tableContainer: {
        maxHeight: 400,
    },
};

export default function UsersTab() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('user');
    const [submitting, setSubmitting] = useState(false);

    // 수정 다이얼로그
    const [editOpen, setEditOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [editUsername, setEditUsername] = useState('');
    const [editPassword, setEditPassword] = useState('');
    const [editRole, setEditRole] = useState('user');
    const [editSubmitting, setEditSubmitting] = useState(false);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const { data } = await adminApi.getUsers();
            setUsers(data.users || []);
        } catch (err) {
            toast.error('사용자 목록 로드 실패');
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            await adminApi.createUser({ username, password, role });
            toast.success('사용자가 추가되었습니다.');
            setUsername('');
            setPassword('');
            setRole('user');
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.error || '사용자 추가 실패');
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenEdit = (user) => {
        setEditUser(user);
        setEditUsername(user.username);
        setEditPassword('');
        setEditRole(user.role);
        setEditOpen(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditSubmitting(true);

        try {
            const updateData = { username: editUsername, role: editRole };
            if (editPassword) {
                updateData.password = editPassword;
            }
            await adminApi.updateUser(editUser.id, updateData);
            toast.success('사용자가 수정되었습니다.');
            setEditOpen(false);
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.error || '사용자 수정 실패');
        } finally {
            setEditSubmitting(false);
        }
    };

    const handleDeleteUser = async (userId, userName) => {
        if (!window.confirm(`${userName} 사용자를 삭제하시겠습니까?`)) return;

        try {
            await adminApi.deleteUser(userId);
            toast.success('사용자가 삭제되었습니다.');
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.error || '사용자 삭제 실패');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h2" sx={styles.header}>
                사용자 목록
            </Typography>

            <Box component="form" onSubmit={handleAddUser} sx={styles.form}>
                <TextField
                    label="사용자명"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    size="small"
                    sx={{ width: 200 }}
                />
                <TextField
                    label="비밀번호"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    size="small"
                    sx={{ width: 200 }}
                />
                <TextField
                    select
                    label="역할"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    size="small"
                    sx={{ width: 140 }}
                >
                    <MenuItem value="user">기술지원팀</MenuItem>
                    <MenuItem value="admin">솔루션랩</MenuItem>
                </TextField>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                >
                    추가
                </Button>
            </Box>

            <TableContainer sx={styles.tableContainer}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>사용자명</TableCell>
                            <TableCell>역할</TableCell>
                            <TableCell>생성일</TableCell>
                            <TableCell>액션</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.id} hover>
                                <TableCell>{user.username}</TableCell>
                                <TableCell>{user.role === 'admin' ? '솔루션랩' : '기술지원팀'}</TableCell>
                                <TableCell>
                                    {new Date(user.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            size="small"
                                            onClick={() => handleOpenEdit(user)}
                                        >
                                            수정
                                        </Button>
                                        {user.username !== 'admin' && (
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteUser(user.id, user.username)}
                                            >
                                                삭제
                                            </Button>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* 사용자 수정 다이얼로그 */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>사용자 수정</DialogTitle>
                <DialogContent>
                    <Box
                        component="form"
                        id="edit-user-form"
                        onSubmit={handleEditSubmit}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
                    >
                        <TextField
                            label="사용자명"
                            value={editUsername}
                            onChange={(e) => setEditUsername(e.target.value)}
                            required
                            size="small"
                            fullWidth
                        />
                        <TextField
                            label="새 비밀번호"
                            type="password"
                            value={editPassword}
                            onChange={(e) => setEditPassword(e.target.value)}
                            size="small"
                            fullWidth
                            placeholder="변경 시에만 입력"
                            helperText="비워두면 기존 비밀번호가 유지됩니다"
                        />
                        <TextField
                            select
                            label="역할"
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value)}
                            size="small"
                            fullWidth
                        >
                            <MenuItem value="user">기술지원팀</MenuItem>
                            <MenuItem value="admin">솔루션랩</MenuItem>
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>취소</Button>
                    <Button
                        type="submit"
                        form="edit-user-form"
                        variant="contained"
                        disabled={editSubmitting}
                    >
                        저장
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
